import React from 'react';
import { RouteComponentProps } from 'react-router';
import { ConnectionsApi } from '../../../apis';
import { FeatureLogger } from '../../../services';
import { DropDownStyles } from '../../../styles/DropDownStyles';
import { defaultFont } from '../../../styles/SharedStyles';
import { IModel, Project, PwWebConection, Service } from '../../../types';
import {
  getLinkToTab,
  getTeamsContext,
  setTeamsSettings,
} from '../../../utils';
import { buildTeamsSettings } from '../../../utils/builders';
import * as Constants from '../../../utils/constants';
import { validateTeamsSettings } from '../../../utils/validators';
import { LoadingPage } from '../../Shared';
import { Config } from './Config';

export interface ConfigContainerProps extends RouteComponentProps<any> {
  project: Project;
  tabIsDefault: boolean;
  service?: Service;
  services: Service[];
  tabName: string;
  defaultRoute?: string;
  connections?: PwWebConection[];
  connectionName?: string;
  connectionId?: string;
  iModel?: IModel;
}

interface ConfigContainerState {
  loading: boolean;
  tabConfigured: boolean;
  inTabSettings: boolean;
}

export class ConfigContainer extends React.Component<
  ConfigContainerProps,
  ConfigContainerState
> {
  state = {
    loading: true,
    tabConfigured: false,
    inTabSettings: false,
  };

  stepCallBack = inTabSettings => {
    this.setState({ inTabSettings });
  };

  async componentDidMount() {
    microsoftTeams.settings.setValidityState(false);
    microsoftTeams.settings.registerOnSaveHandler(this.onSave);

    const context = await getTeamsContext();
    if (context.entityId) {
      this.setState({ tabConfigured: true });
    }

    this.setState({ loading: false });
  }

  componentDidUpdate() {
    const validation = validateTeamsSettings(this.state.tabConfigured, {
      projectId: this.props.project.id,
      services: this.props.services,
      tabName: this.props.tabName,
      connections: this.props.connections,
      connectionName: this.props.connectionName,
      iModel: this.props.iModel,
    });

    microsoftTeams.settings.setValidityState(
      validation.projectsValid &&
        validation.serviceValid &&
        !this.state.inTabSettings &&
        (this.props.project.solutionType ===
          Constants.SolutionTypes.projectWise365 ||
          this.props.service?.displayName !== 'ProjectWise Web Connections' ||
          validation.connectionsValid) &&
        (this.props.service?.id !== Constants.ServiceRegistry.designReviewId ||
          validation.iModelValid)
    );
  }

  setDefaultTabForConnections = async (
    settings: microsoftTeams.settings.Settings
  ) => {
    if (
      (!this.props.tabIsDefault && !this.state.tabConfigured) ||
      !this.props.connections
    ) {
      console.error(this.state.tabConfigured);
      return;
    }

    const link = this.props.tabIsDefault
      ? await getLinkToTab(settings.entityId)
      : undefined;

    const projectId = this.props.project.id;
    const connectionIds =
      this.props.project.solutionType === Constants.SolutionTypes.projectWise365
        ? this.props.connections.map(connection => connection.id)
        : [this.props.connectionId!];

    if (link) {
      return ConnectionsApi.createDefaultTabForConnections(
        projectId,
        connectionIds,
        link
      );
    }

    return ConnectionsApi.deleteDefaultTabForConnections(
      projectId,
      connectionIds
    );
  };

  onSave = async saveEvent => {
    try {
      const settings = await buildTeamsSettings(
        this.props.tabName,
        this.props.project.id,
        this.props.services[0],
        this.props.defaultRoute,
        this.props.connectionId,
        this.props.connectionName,
        this.props.iModel
      );

      await setTeamsSettings(settings);
      await this.setDefaultTabForConnections(settings);
      FeatureLogger.markFeature(
        this.props.project.id,
        'add',
        'ProjectWise Web Connections',
        this.props.defaultRoute
      );

      saveEvent.notifySuccess();
    } catch (e) {
      await saveEvent.notifyFailure(e);
      console.error(e);
    }
  };

  render() {
    return !this.state.loading ? (
      <div
        style={{
          ...defaultFont,
          fontWeight: 400,
          height: '100%',
        }}
      >
        <DropDownStyles />
        <Config
          isPw365Project={
            this.props.project.solutionType ===
            Constants.SolutionTypes.projectWise365
          }
          tabConfigured={this.state.tabConfigured}
          stepCallBack={this.stepCallBack}
        />
      </div>
    ) : (
      <LoadingPage subtext='Loading tab configuration...' />
    );
  }
}
